import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { deleteUserAdmin, getUserById, reactivateUser } from '../../store/users';
import { canModifyUser } from '../../utils/permissions';

function UserDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionUser = useSelector((state) => state.session.user);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await dispatch(getUserById(id));
        setUser(userData);
      } catch (error) {
        console.error("Error fetching user:", error);
        setError("Failed to load user details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id, dispatch]);

  const handleDelete = async () => {
    if (sessionUser.id === user.id) {
      setError("You cannot delete your own account. Please contact an administrator for account deletion.");
      return;
    }

    if (!canModifyUser(sessionUser, user, 'delete')) {
      setError("You are not authorized to delete the user.");
      return;
    }

    if (window.confirm('Are you sure you want to deactivate this user?')) {
      try {
        await dispatch(deleteUserAdmin(id));
        // Refresh user data after deactivation
        const userData = await dispatch(getUserById(id));
        setUser(userData);
      } catch (error) {
        console.error('Error deactivating user:', error);
        setError("An error occurred while deactivating the user.");
      }
    }
  };

  const handleReactivate = async () => {
    if (!sessionUser.is_admin) {
      setError("Only administrators can reactivate users.");
      return;
    }

    if (window.confirm('Are you sure you want to reactivate this user?')) {
      try {
        await dispatch(reactivateUser(id));
        // Refresh user data after reactivation
        const userData = await dispatch(getUserById(id));
        setUser(userData);
      } catch (error) {
        console.error('Error reactivating user:', error);
        setError("An error occurred while reactivating the user.");
      }
    }
  };

  if (loading) {
    return <div className="p-5 text-7xl">Loading...</div>;
  }

  if (!user) {
    return <div className="p-5 text-7xl">User not found</div>;
  }

  const getUserRole = () => {
    if (user.is_admin) return 'Admin';
    if (user.is_super_manager) return 'Super Manager';
    if (user.is_account_manager) return 'Account Manager';
    if (user.is_manager) return 'Manager';
    if (user.is_yard_driver) return 'Yard Driver';
    if (user.is_road_driver) return 'Road Driver';
    if (user.is_mechanic) return 'Mechanic';
    if (user.is_security) return 'Security';
    return 'User';
  };

  return (
    <div className="p-5 min-h-[calc(100vh-100px)] flex flex-col">
      <div className="max-w-6xl mx-auto w-full bg-white rounded-lg shadow-md p-8">
        <div className="mb-8">
          <h2 className="text-6xl font-bold text-blue-600">User Detail</h2>
          <div className="mt-2 flex gap-2">
            <div className="inline-block px-3 py-1 rounded-full text-xl bg-blue-100 text-blue-800">
              {getUserRole()}
            </div>
            <div className={`inline-block px-3 py-1 rounded-full text-xl ${
              user.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {user.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 text-2xl" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {/* General Information */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="text-4xl font-semibold mb-4">General Information</h3>
          <div className="space-y-4 text-2xl">
            <p className="flex items-center">
              <span className="font-semibold w-40">Email:</span>
              <span>{user.email}</span>
            </p>
            <p className="flex items-center">
              <span className="font-semibold w-40">Alpha Code:</span>
              <span>{user.alpha_code}</span>
            </p>
            {user.driver_number && (
              <p className="flex items-center">
                <span className="font-semibold w-40">Driver Number:</span>
                <span>{user.driver_number}</span>
              </p>
            )}
            <p className="flex items-center">
              <span className="font-semibold w-40">Role:</span>
              <span className="flex items-center">
                <span className="h-3 w-3 rounded-full mr-2 bg-blue-500"></span>
                {getUserRole()}
              </span>
            </p>
            <p className="flex items-center">
              <span className="font-semibold w-40">Status:</span>
              <span className={`flex items-center ${
                user.is_active ? 'text-green-600' : 'text-red-600'
              }`}>
                <span className={`h-3 w-3 rounded-full mr-2 ${
                  user.is_active ? 'bg-green-500' : 'bg-red-500'
                }`}></span>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
            </p>
            <p className="flex items-center">
              <span className="font-semibold w-40">Created:</span>
              <span>{new Date(user.created_at).toLocaleString()}</span>
            </p>
            <p className="flex items-center">
              <span className="font-semibold w-40">Last Updated:</span>
              <span>
                {user.updated_at
                  ? new Date(user.updated_at).toLocaleString()
                  : new Date(user.created_at).toLocaleString()}
              </span>
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <button
            id='back-user-button'
            onClick={() => history.push('/')}
            className="px-8 py-4 text-3xl text-gray-700 bg-gray-200 rounded-lg transition-all duration-200
              hover:bg-gray-300 hover:shadow-md hover:scale-[1.02]"
          >
            Back
          </button>
          <div className="space-x-4">
            {user.is_active ? (
              <>
                <button
                  id='update-user-button'
                  onClick={() => history.push(`/users/${id}/edit`)}
                  className="inline-block px-6 py-3 text-2xl text-white bg-blue-500 rounded-lg transition-all duration-200
                    hover:bg-blue-600 hover:shadow-md hover:scale-[1.02]"
                  disabled={!canModifyUser(sessionUser, user, 'update')}
                >
                  Update
                </button>
                {canModifyUser(sessionUser, user, 'delete') && (
                  <button
                    id='deactivate-user-button'
                    onClick={handleDelete}
                    className="px-6 py-3 text-2xl text-white bg-red-500 rounded-lg transition-all duration-200
                      hover:bg-red-600 hover:shadow-md hover:scale-[1.02]"
                  >
                    Deactivate
                  </button>
                )}
              </>
            ) : (
              sessionUser.is_admin && (
                <button
                  id='reactivate-user-button'
                  onClick={handleReactivate}
                  className="px-6 py-3 text-2xl text-white bg-green-500 rounded-lg transition-all duration-200
                    hover:bg-green-600 hover:shadow-md hover:scale-[1.02]"
                >
                  Reactivate
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDetail;
